import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useMatchRoute } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import {
  MarkdownContent,
  MarkdownModeToggle,
  type MarkdownDisplayMode,
} from '../../components/content/MarkdownContent';
import { Icon } from '../../components/ui/Icon';
import { GoalEditor } from '../../components/content/GoalEditor';
import { GmailPreferences } from '../../components/email/GmailPreferences';
import { api } from '../../lib/api-client';
import type { TokenUsageRequest, TokenUsageSource, TokenUsageTotals, UsageCategory, UsageLimit } from '../../lib/api-types';
import { useAuth } from '../../lib/auth';
import {
  readPersonalAnthropicApiKey,
  removePersonalAnthropicApiKey,
} from '../../lib/personal-api-key';
import { billingUsageQuery, dailyBriefQuery, keys, personalizationQuery } from '../../lib/queries';
import { LearningHistory } from '../../components/chat/LearningNotice';
import { THEME_COLORS, useTheme, type Appearance, type ThemeColor } from '../../lib/theme';
import styles from './Pages.module.css';

type SettingsPath =
  | '/app/settings'
  | '/app/settings/theme'
  | '/app/settings/ai-memory'
  | '/app/settings/goals'
  | '/app/settings/ai-provider'
  | '/app/settings/gmail'
  | '/app/settings/billing-usage';

const settingsItems: ReadonlyArray<{
  to: SettingsPath;
  icon: string;
  label: string;
  subtitle: string;
  section: 'Personal' | 'Account';
}> = [
  { to: '/app/settings/theme', icon: 'sun-moon', label: 'Theme', subtitle: 'Appearance and glow', section: 'Personal' },
  { to: '/app/settings/ai-memory', icon: 'brain', label: 'AI Memory', subtitle: 'Context used across chats', section: 'Personal' },
  { to: '/app/settings/goals', icon: 'target-arrow', label: 'Goals', subtitle: 'What Resonance helps accomplish', section: 'Personal' },
  { to: '/app/settings/ai-provider', icon: 'key', label: 'AI Provider', subtitle: 'Managed or personal API key', section: 'Account' },
  { to: '/app/settings/gmail', icon: 'mail', label: 'Gmail', subtitle: 'Manage or disconnect your mailbox', section: 'Account' },
  { to: '/app/settings/billing-usage', icon: 'credit-card', label: 'Billing & Usage', subtitle: 'Credits, limits, and subscription', section: 'Account' },
];

export function SettingsLayout() {
  const { bootstrap } = useAuth();
  const matchRoute = useMatchRoute();

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>
          <span className={styles.titleIcon}><Icon name="settings" size={20} /></span>
          Settings
        </h1>
      </header>

      <div className={`${styles.body} ${styles.settingsBody}`}>
        <div className={styles.settingsLayout}>
          <nav className={styles.settingsNav} aria-label="Settings">
            <Link
              to="/app/settings"
              className={styles.settingsNavHome}
              data-active={Boolean(matchRoute({ to: '/app/settings' }))}
            >
              <span className={styles.settingsNavIcon}><Icon name="user" size={18} /></span>
              <span>
                <strong>{bootstrap?.user?.email ?? 'Account'}</strong>
                <small>{bootstrap?.workspace.displayName ?? 'Workspace'}</small>
              </span>
            </Link>

            {(['Personal', 'Account'] as const).map((section) => (
              <div className={styles.settingsNavSection} key={section}>
                <h2 className={styles.sectionTitle}>{section}</h2>
                {settingsItems.filter((item) => item.section === section).map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={styles.settingsNavItem}
                    data-active={Boolean(matchRoute({ to: item.to }))}
                  >
                    <span className={styles.settingsNavIcon}><Icon name={item.icon} size={18} /></span>
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            ))}

          </nav>

          <main className={styles.settingsContent}>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}

export function SettingsHomePage() {
  const { bootstrap, signOut } = useAuth();
  return (
    <SettingsPageIntro
      title="Your settings"
      description="Personalize how Resonance works, review your usage, or manage your account."
    >
      <div className={styles.settingsCards}>
        {settingsItems.map((item) => (
          <Link key={item.to} to={item.to} className={styles.settingsCardLink}>
            <span className={styles.settingsCardIcon}><Icon name={item.icon} size={21} /></span>
            <span className={styles.settingsCardCopy}>
              <strong>{item.label}</strong>
              <small>{item.subtitle}</small>
            </span>
            <Icon name="chevron-right" size={16} />
          </Link>
        ))}
      </div>

      <section className={styles.settingsCard}>
        <h2>Account</h2>
        <SettingsValue label="Signed in as" value={bootstrap?.user?.email ?? '—'} />
        <SettingsValue label="Workspace" value={bootstrap?.workspace.displayName ?? '—'} />
        <SettingsValue label="Model" value={bootstrap?.model.displayName ?? '—'} />
        <button type="button" className={styles.accountSignOut} onClick={signOut}>
          <Icon name="logout" size={18} />
          Sign out
        </button>
      </section>
    </SettingsPageIntro>
  );
}

export function GmailSettingsPage() {
  return (
    <SettingsPageIntro title="Gmail" description="Manage your mailbox, automatic replies, and connection.">
      <GmailPreferences />
    </SettingsPageIntro>
  );
}

export function ThemeSettingsPage() {
  const { appearance, themeColor, setAppearance, setThemeColor } = useTheme();
  const appearances: ReadonlyArray<{ value: Appearance; title: string; description: string; icon: string }> = [
    { value: 'system', title: 'System', description: 'Follow this device’s appearance', icon: 'sun-moon' },
    { value: 'dark', title: 'Dark', description: 'Use the dark appearance', icon: 'moon' },
    { value: 'light', title: 'Light', description: 'Use the light appearance', icon: 'sun' },
  ];

  return (
    <SettingsPageIntro title="Theme" description="Choose how Resonance appears in this browser.">
      <SettingsSection title="Mode">
        <div className={styles.optionList}>
          {appearances.map((option) => (
            <OptionButton
              key={option.value}
              icon={option.icon}
              title={option.title}
              description={option.description}
              selected={appearance === option.value}
              onClick={() => setAppearance(option.value)}
            />
          ))}
        </div>
      </SettingsSection>

      <SettingsSection title="Color">
        <div className={styles.optionList}>
          {THEME_COLORS.map((color) => (
            <ColorOption key={color} color={color} selected={themeColor === color} onClick={() => setThemeColor(color)} />
          ))}
        </div>
      </SettingsSection>
    </SettingsPageIntro>
  );
}

export function AiMemorySettingsPage() {
  return (
    <PersonalizationEditor
      field="personalization"
      title="AI Memory"
      description="Durable context Resonance has learned about how you work, your preferences, and your business."
      placeholder="No personalization saved yet."
      saveLabel="Save Memory"
    />
  );
}

export function GoalsSettingsPage() {
  const personal = useQuery(personalizationQuery());
  return (
    <SettingsPageIntro title="Goals" description="The outcomes Resonance should keep in mind when helping you prioritize, plan, and act.">
      {personal.isLoading && <SettingsState>Loading…</SettingsState>}
      {personal.isError && <SettingsError error={personal.error} />}
      {personal.data && <GoalEditor personal={personal.data} />}
      <LearningHistory />
      <DailyProgressPreference />
    </SettingsPageIntro>
  );
}

function DailyProgressPreference() {
  const client = useQueryClient();
  const daily = useQuery(dailyBriefQuery());
  const change = useMutation({ mutationFn: api.setDailyBriefEnabled,
    onSuccess: result => {
      client.setQueryData(keys.dailyBrief, previous => previous ? { ...previous, enabled: result.enabled } : previous);
      void client.invalidateQueries({ queryKey: keys.dailyBrief });
    },
  });
  return <section className={styles.settingsCard} aria-label="Daily Auto Progress">
    <h2>Daily Auto Progress</h2>
    <p>Runs each morning for this workspace. Gmail reply drafts have their own control in <a href="/app/settings/gmail">Gmail settings</a>.</p>
    {daily.data && <button type="button" disabled={change.isPending} onClick={() => change.mutate(!daily.data!.enabled)}>
      {change.isPending ? 'Saving…' : daily.data.enabled ? 'Turn off daily Auto Progress' : 'Enable daily Auto Progress'}
    </button>}
    {daily.isError && <p role="alert">Couldn’t load this setting. <button onClick={() => void daily.refetch()}>Try again</button></p>}
    {change.error && <p role="alert">{change.error.message}</p>}
  </section>;
}

function PersonalizationEditor({
  field, title, description, placeholder, saveLabel,
}: {
  field: 'personalization' | 'goals';
  title: string;
  description: string;
  placeholder: string;
  saveLabel: string;
}) {
  const queryClient = useQueryClient();
  const personalization = useQuery(personalizationQuery());
  const [draft, setDraft] = useState('');
  const [hasEdited, setHasEdited] = useState(false);
  const [saved, setSaved] = useState(false);
  const [displayMode, setDisplayMode] = useState<MarkdownDisplayMode>('preview');

  useEffect(() => {
    if (!hasEdited && personalization.data) setDraft(personalization.data[field]);
  }, [field, hasEdited, personalization.data]);

  const mutation = useMutation({
    mutationFn: (value: string) => api.updatePersonalization(
      field === 'personalization' ? { personalization: value } : { goals: value },
    ),
    onSuccess: (result) => {
      queryClient.setQueryData(keys.personalization, result);
      setHasEdited(false);
      setSaved(true);
    },
  });

  return (
    <SettingsPageIntro title={title} description={description}>
      {personalization.isLoading ? <SettingsState>Loading…</SettingsState> : null}
      {personalization.isError ? <SettingsError error={personalization.error} /> : null}
      {personalization.data ? (
        <form
          className={styles.documentForm}
          onSubmit={(event) => {
            event.preventDefault();
            setSaved(false);
            mutation.mutate(draft);
          }}
        >
          <MarkdownModeToggle mode={displayMode} onChange={setDisplayMode} label={`${title} view`} />
          {displayMode === 'preview' ? (
            draft.trim()
              ? <div className={styles.documentPreview}><MarkdownContent source={draft} /></div>
              : <div className={styles.documentPreviewEmpty}>{placeholder}</div>
          ) : (
            <textarea
              value={draft}
              placeholder={placeholder}
              onChange={(event) => {
                setDraft(event.target.value);
                setHasEdited(true);
                setSaved(false);
              }}
              aria-label={`${title} Markdown`}
            />
          )}
          {mutation.isError ? <SettingsError error={mutation.error} /> : null}
          {saved ? <p className={styles.success}>Saved</p> : null}
          <button type="submit" className={styles.primaryButton} disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : saveLabel}
          </button>
        </form>
      ) : null}
      <LearningHistory />
    </SettingsPageIntro>
  );
}

export function AiProviderSettingsPage() {
  const { bootstrap } = useAuth();
  const client = useQueryClient();
  const queryKey = ['ai-provider', bootstrap?.workspace.id, bootstrap?.user?.id];
  const provider = useQuery({ queryKey, queryFn: api.aiProvider });
  const [keyDraft, setKeyDraft] = useState('');
  const [browserKey, setBrowserKey] = useState(() => readPersonalAnthropicApiKey());
  const [status, setStatus] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: async (action: 'save' | 'enable' | 'disable' | 'remove' | 'migrate') => {
      const result = action === 'save' || action === 'migrate'
        ? await api.saveAiProviderKey(action === 'migrate' ? browserKey ?? '' : keyDraft.trim())
        : action === 'remove' ? await api.removeAiProviderKey()
        : await api.setAiProviderEnabled(action === 'enable');
      removePersonalAnthropicApiKey();
      return result;
    },
    onSuccess: (result) => {
      client.setQueryData(queryKey, result);
      setBrowserKey(null);
      setKeyDraft('');
      setStatus(result.enabled ? 'Your key now powers chat, Auto Progress and email drafts.' : 'Resonance billing is active.');
    },
  });
  const modelMutation = useMutation({ mutationFn: api.setAiProviderModel, onSuccess: result => client.setQueryData(queryKey, result) });
  const saved = provider.data?.saved;
  return (
    <SettingsPageIntro title="AI Provider" description="Use your Anthropic or OpenAI key across Resonance, including daily Auto Progress and email drafts. Usage is tracked either way.">
      {provider.isLoading ? <SettingsState>Loading…</SettingsState> : null}
      {provider.isError ? <SettingsError error={provider.error} /> : null}
      {provider.data ? <section className={styles.settingsCard}>
        <label className={styles.toggleRow}>
          <span><strong>Use my API key</strong><small>{provider.data.enabled ? 'Billed directly by your model provider · no Resonance credits needed' : 'Resonance billing is active'}</small></span>
          <input type="checkbox" checked={provider.data.enabled} disabled={!saved || mutation.isPending} onChange={event => mutation.mutate(event.target.checked ? 'enable' : 'disable')} />
        </label>
        {saved ? <p className={styles.keyStatus}><Icon name="lock" size={13} /> {provider.data.provider === 'openai' ? 'OpenAI' : 'Anthropic'} · Stored encrypted · ends in {provider.data.suffix}</p> : null}
        {saved && provider.data.models?.length ? <label>Default model
          <select aria-label="Default model" value={provider.data.defaultModel ?? ''} disabled={modelMutation.isPending} onChange={e => modelMutation.mutate(e.target.value)}>
            {provider.data.provider !== 'openai' && <option value="" disabled>Workflow default</option>}
            {provider.data.models.map(model => <option key={model.id} value={model.id}>{model.displayName}</option>)}
          </select>
          <p className={styles.helpText}>Used by Auto Progress, email drafts, memory updates, and chats set to Default.</p>
        </label> : null}
        {modelMutation.error ? <SettingsError error={modelMutation.error} /> : null}
        {browserKey ? <><p className={styles.helpText}>Your existing browser key needs to be saved securely for scheduled work.</p><button className={styles.primaryButton} disabled={mutation.isPending} onClick={() => mutation.mutate('migrate')}>Use existing key across Resonance</button></> : null}
      </section> : null}
      <section className={styles.settingsCard}>
        <h2>{saved ? 'Replace API key' : 'Add API key'}</h2>
        <input className={styles.keyInput} type="password" value={keyDraft} placeholder="sk-ant-… or sk-proj-…" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="off" onChange={event => setKeyDraft(event.target.value)} aria-label="API key" />
        <p className={styles.helpText}>One personal key is active at a time. Saving a new key replaces the previous key. Stored encrypted for your account so enabled automations work while you’re away. Disabling or removing it switches future runs to Resonance billing.</p>
        <button type="button" className={styles.primaryButton} disabled={!keyDraft.trim() || mutation.isPending || !provider.data} onClick={() => mutation.mutate('save')}>Save and Use This Key</button>
      </section>
      {mutation.error ? <p className={styles.error}>{errorMessage(mutation.error)}</p> : null}
      {status ? <p className={styles.success}>{status}</p> : null}
      {saved ? <button type="button" className={styles.dangerButton} disabled={mutation.isPending} onClick={() => {
        if (window.confirm('Remove this key? Future AI work will use Resonance credits.')) mutation.mutate('remove');
      }}>Remove Personal API Key</button> : null}
    </SettingsPageIntro>
  );
}

export function BillingUsageSettingsPage() {
  const billing = useQuery(billingUsageQuery());

  return (
    <SettingsPageIntro
      title="Billing & Usage"
      description="Your Resonance subscription, AI credit balance, usage breakdown, and configured limits."
    >
      {billing.isLoading ? <SettingsState>Loading…</SettingsState> : null}
      {billing.isError ? <SettingsError error={billing.error} /> : null}
      {billing.data ? (
        <>
          {billing.data.alerts.map((alert) => (
            <div key={alert.code} className={styles.billingAlert} data-type={alert.type}>{alert.message}</div>
          ))}

          <div className={styles.billingGrid}>
            <section className={styles.settingsCard}>
              <h2>Credit Balance</h2>
              <strong className={styles.bigNumber}>{formatNumber(billing.data.creditBalance.remaining)}</strong>
              <p className={styles.helpText}>
                {formatNumber(billing.data.creditBalance.used)} of {formatNumber(billing.data.creditBalance.allocated)} credits used
              </p>
            </section>
            <section className={styles.settingsCard}>
              <h2>Subscription</h2>
              <strong className={styles.planName}>{titleCase(billing.data.subscription.tier)}</strong>
              <p className={styles.planStatus}>{titleCase(billing.data.subscription.status.replaceAll('_', ' '))}</p>
              {billing.data.subscription.periodEnd ? (
                <p className={styles.helpText}>
                  {billing.data.subscription.daysRemaining} days remaining · renews {formatDate(billing.data.subscription.periodEnd)}
                </p>
              ) : null}
            </section>
          </div>

          {billing.data.tokenUsage ? (
            <SettingsSection title="Model Tokens">
              <section className={styles.settingsCard}>
                <TokenSummaryTable
                  total={billing.data.tokenUsage}
                  chat={billing.data.tokenUsage.bySource.chat}
                  auto={billing.data.tokenUsage.bySource.auto}
                />
              </section>

              <section className={styles.settingsCard}>
                <div className={styles.tokenRequestHeader}>
                  <div>
                    <h2>Recent model requests</h2>
                    <p className={styles.helpText}>Each row is one model request, including requests inside tool loops.</p>
                  </div>
                  <span>{billing.data.tokenUsage.recentRequests.length === billing.data.tokenUsage.recentRequestLimit
                    ? `Latest ${billing.data.tokenUsage.recentRequestLimit}`
                    : 'This month'}</span>
                </div>
                {billing.data.tokenUsage.recentRequests.length ? (
                  <RecentTokenRequestsTable requests={billing.data.tokenUsage.recentRequests} />
                ) : <p className={styles.helpText}>No model requests this month.</p>}
              </section>
            </SettingsSection>
          ) : null}

          <SettingsSection title="Usage">
            <section className={styles.settingsCard}>
              <ProgressRow title="Compute" detail={`${formatNumber(billing.data.usage.compute.credits)} credits`} usage={billing.data.usage.compute} />
              <ProgressRow title="Enhancements" detail={`${formatNumber(billing.data.usage.enhancements.credits)} credits`} usage={billing.data.usage.enhancements} />
              <ProgressRow title="Total" detail={`${formatNumber(billing.data.usage.total.credits)} credits`} usage={billing.data.usage.total} />
            </section>
          </SettingsSection>

          <SettingsSection title="Limits">
            <section className={styles.settingsCard}>
              <LimitRow title="Daily" limit={billing.data.limits.daily} />
              <LimitRow title="Monthly" limit={billing.data.limits.monthly} />
            </section>
          </SettingsSection>

          <button type="button" className={styles.secondaryButton} onClick={() => void billing.refetch()} disabled={billing.isFetching}>
            {billing.isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </>
      ) : null}
    </SettingsPageIntro>
  );
}

function SettingsPageIntro({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <div className={styles.settingsPage}>
      <div className={styles.settingsIntro}>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {children}
    </div>
  );
}

function SettingsSection({ title, children }: { title: string; children: ReactNode }) {
  return <section className={styles.section}><h2 className={styles.sectionTitle}>{title}</h2>{children}</section>;
}

function OptionButton({
  icon, title, description, selected, onClick,
}: { icon: string; title: string; description: string; selected: boolean; onClick: () => void }) {
  return (
    <button type="button" className={styles.optionButton} data-selected={selected} onClick={onClick}>
      <span className={styles.settingsCardIcon}><Icon name={icon} size={21} /></span>
      <span className={styles.settingsCardCopy}><strong>{title}</strong><small>{description}</small></span>
      {selected ? <Icon name="check" size={16} /> : null}
    </button>
  );
}

const colorNames: Record<ThemeColor, { title: string; description: string }> = {
  default: { title: 'Default', description: 'Adaptive neutral interface' },
  blue: { title: 'Blue', description: 'Clear and focused' },
  purple: { title: 'Purple', description: 'Creative and expressive' },
  red: { title: 'Red', description: 'Bold and energetic' },
  pink: { title: 'Pink', description: 'Warm and vivid' },
  orange: { title: 'Orange', description: 'Bright and optimistic' },
  yellow: { title: 'Yellow', description: 'Light and lively' },
  teal: { title: 'Teal', description: 'Calm and balanced' },
};

function ColorOption({ color, selected, onClick }: { color: ThemeColor; selected: boolean; onClick: () => void }) {
  return (
    <button type="button" className={styles.optionButton} data-selected={selected} onClick={onClick}>
      <span className={styles.colorDot} data-color={color} />
      <span className={styles.settingsCardCopy}>
        <strong>{colorNames[color].title}</strong><small>{colorNames[color].description}</small>
      </span>
      {selected ? <Icon name="check" size={16} /> : null}
    </button>
  );
}

function SettingsValue({ label, value, emphasized = false }: { label: string; value: string; emphasized?: boolean }) {
  return <div className={styles.valueRow} data-emphasized={emphasized}><span>{label}</span><strong>{value}</strong></div>;
}

function TokenSummaryTable({
  total, chat, auto,
}: { total: TokenUsageTotals; chat: TokenUsageTotals; auto: TokenUsageTotals }) {
  const columns = [total, chat, auto];
  const rows: Array<{ label: string; value: (usage: TokenUsageTotals) => string; emphasized?: boolean }> = [
    { label: 'Requests', value: (usage) => formatNumber(usage.requests) },
    { label: 'Uncached input', value: (usage) => formatNumber(usage.inputTokens) },
    { label: 'Cache reads', value: (usage) => formatNumber(usage.cacheReadTokens) },
    { label: 'Cache writes', value: (usage) => formatNumber(usage.cacheWriteTokens) },
    { label: 'Output', value: (usage) => formatNumber(usage.outputTokens) },
    { label: 'Total tokens', value: (usage) => formatNumber(usage.totalTokens), emphasized: true },
    { label: 'Cache-read share', value: (usage) => formatPercentage(usage.cacheReadPercentage), emphasized: true },
    { label: 'Estimated cost', value: (usage) => formatUsd(usage.estimatedCostUsd), emphasized: true },
  ];
  return (
    <div className={styles.tokenTableScroller}>
      <table className={styles.tokenSummaryTable}>
        <thead><tr><th scope="col">Metric</th><th scope="col">Total</th><th scope="col">Chat</th><th scope="col">Background</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} data-emphasized={row.emphasized || undefined}>
              <th scope="row">{row.label}</th>
              {columns.map((usage, index) => <td key={index}>{row.value(usage)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecentTokenRequestsTable({ requests }: { requests: TokenUsageRequest[] }) {
  return (
    <div className={styles.tokenTableScroller}>
      <table className={styles.tokenRequestTable}>
        <thead>
          <tr>
            <th scope="col">When</th><th scope="col">Type</th><th scope="col">Model</th>
            <th scope="col">Est. cost</th><th scope="col">Input</th><th scope="col">Cache read</th><th scope="col">Cache write</th><th scope="col">Output</th><th scope="col">Total</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((request) => (
            <tr key={request.id}>
              <td>{formatDateTime(request.createdAt)}</td>
              <td><span className={styles.tokenSourceBadge} data-source={request.source}>{usagePurposeLabel(request)}</span></td>
              <td>{request.model}</td>
              <td>{formatUsd(request.estimatedCostUsd)}</td>
              <td>{formatNumber(request.inputTokens)}</td>
              <td>{formatNumber(request.cacheReadTokens)}</td>
              <td>{formatNumber(request.cacheWriteTokens)}</td>
              <td>{formatNumber(request.outputTokens)}</td>
              <td>{formatNumber(request.totalTokens)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProgressRow({ title, detail, usage }: { title: string; detail: string; usage: UsageCategory }) {
  return <Progress title={title} detail={detail} percentage={usage.percentage} />;
}

function LimitRow({ title, limit }: { title: string; limit: UsageLimit }) {
  const detail = limit.limit === null
    ? `${formatNumber(limit.used)} used`
    : `${formatNumber(limit.used)} of ${formatNumber(limit.limit)}`;
  return <Progress title={title} detail={detail} percentage={limit.percentage ?? 0} />;
}

function Progress({ title, detail, percentage }: { title: string; detail: string; percentage: number }) {
  const width = Math.min(100, Math.max(0, percentage));
  return (
    <div className={styles.progressRow}>
      <div><strong>{title}</strong><span>{detail}</span></div>
      <div className={styles.progressTrack}><span style={{ width: `${width}%` }} /></div>
    </div>
  );
}

function SettingsState({ children }: { children: ReactNode }) {
  return <div className={styles.settingsState}>{children}</div>;
}

function SettingsError({ error }: { error: unknown }) {
  return <p className={styles.error}>{errorMessage(error)}</p>;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong.';
}

const numberFormatter = new Intl.NumberFormat();
const percentageFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1, style: 'percent' });
const usdFormatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 });

function formatNumber(value: number): string { return numberFormatter.format(value); }
function formatPercentage(value: number): string { return percentageFormatter.format(value / 100); }
function formatUsd(value: number): string { return usdFormatter.format(value); }
function formatDate(value: string): string { return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value)); }
function formatDateTime(value: string): string { return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)); }
function sourceLabel(source: TokenUsageSource): string { return source === 'auto' ? 'Background' : source === 'chat' ? 'Chat' : 'Other'; }
function usagePurposeLabel(request: TokenUsageRequest): string {
  const labels: Record<string, string> = { email_reply: 'Auto reply', email_outreach: 'Email outreach', email_automation: 'Sequence drafting', email_guidance_preview: 'Email preview' };
  return (request.purpose && labels[request.purpose]) || sourceLabel(request.source);
}
function titleCase(value: string): string { return value.replace(/\b\w/g, (character) => character.toUpperCase()); }
